import type { CommandProps } from '@tiptap/core';
import type { ResolvedPos } from '@tiptap/pm/model';
import { Extension } from '@tiptap/react';
import { ALIGNABLE_NODES } from './textAlignClass';

export const MAX_INDENT = 3;

const CLASS_BY_LEVEL = ['', 'ml-10', 'ml-20', 'ml-30'] as const;

declare module '@tiptap/core' {
    interface Commands<ReturnType> {
        indentClass: {
            indent: () => ReturnType;
            outdent: () => ReturnType;
        };
    }
}

const indentFromClassList = (element: HTMLElement): number => {
    const level = CLASS_BY_LEVEL.findIndex((className, index) => index > 0 && element.classList.contains(className));
    return Math.max(level, 0);
};

const isInListItem = ($pos: ResolvedPos): boolean => {
    for (let depth = $pos.depth; depth > 0; depth -= 1) {
        if ($pos.node(depth).type.name === 'listItem') return true;
    }
    return false;
};

export const IndentClass = Extension.create({
    name: 'indentClass',
    addGlobalAttributes() {
        return [
            {
                types: [...ALIGNABLE_NODES],
                attributes: {
                    indent: {
                        default: 0,
                        parseHTML: indentFromClassList,
                        renderHTML: (attributes) => {
                            const className = CLASS_BY_LEVEL[attributes.indent as number];
                            return className ? { class: className } : {};
                        },
                    },
                },
            },
        ];
    },
    addCommands() {
        const shift =
            (delta: 1 | -1) =>
            () =>
            ({ state, tr, dispatch, commands }: CommandProps) => {
                if (isInListItem(state.selection.$from)) {
                    return delta === 1 ? commands.sinkListItem('listItem') : commands.liftListItem('listItem');
                }
                let changed = false;
                state.doc.nodesBetween(state.selection.from, state.selection.to, (node, pos) => {
                    if (!(ALIGNABLE_NODES as readonly string[]).includes(node.type.name)) return;
                    const current = node.attrs.indent as number;
                    const next = Math.min(Math.max(current + delta, 0), MAX_INDENT);
                    if (next === current) return;
                    changed = true;
                    if (dispatch) tr.setNodeMarkup(pos, undefined, { ...node.attrs, indent: next });
                });
                if (changed && dispatch) dispatch(tr);
                return changed;
            };
        return { indent: shift(1), outdent: shift(-1) };
    },
});
